import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import {
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { ChangeEvent, memo } from 'react';

import { userLib } from 'entities/user';

import { EUserDeletionSource } from 'shared/api';

type Props = {
  filter: string;
  source: EUserDeletionSource | '';
  from: Date | null;
  to: Date | null;
  totalCount: number;
  isFetching?: boolean;
  onFilterChange: (value: string) => void;
  onSourceChange: (value: EUserDeletionSource | '') => void;
  onFromChange: (date: Date | null) => void;
  onToChange: (date: Date | null) => void;
};

export const DeletedUsersFilters = memo(
  ({
    filter,
    source,
    from,
    to,
    totalCount,
    isFetching = false,
    onFilterChange,
    onSourceChange,
    onFromChange,
    onToChange,
  }: Props) => {
    const handleFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
      onFilterChange(e.target.value);
    };

    const handleClearFilter = () => {
      onFilterChange('');
    };

    const handleSourceChange = (e: SelectChangeEvent) => {
      onSourceChange(e.target.value as EUserDeletionSource | '');
    };

    return (
      <Paper sx={{ mb: 2 }}>
        <Box display="flex" alignItems="center" p="8px 16px" gap={2} flexWrap="wrap">
          <Typography variant="h6" sx={{ minWidth: 'fit-content' }}>
            Deleted users
          </Typography>

          <Box display="flex" alignItems="center" gap={2} flex={1} minWidth="300px" flexWrap="wrap">
            <TextField
              sx={{ minWidth: '300px', maxWidth: '500px', flex: 1 }}
              placeholder="Search by email..."
              size="small"
              value={filter}
              onChange={handleFilterChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: filter && (
                  <IconButton size="small" onClick={handleClearFilter}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ),
              }}
            />

            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>Source</InputLabel>
              <Select value={source} label="Source" onChange={handleSourceChange}>
                <MenuItem value="">All</MenuItem>
                {Object.values(EUserDeletionSource).map((s) => (
                  <MenuItem key={s} value={s}>
                    {userLib.DELETION_SOURCE_META[s].label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <DatePicker
              label="From"
              value={from}
              onChange={onFromChange}
              maxDate={to ?? undefined}
              slotProps={{
                textField: { size: 'small', sx: { width: 170 } },
                field: { clearable: true },
              }}
              format="dd MMM yyyy"
            />
            <DatePicker
              label="To"
              value={to}
              onChange={onToChange}
              minDate={from ?? undefined}
              slotProps={{
                textField: { size: 'small', sx: { width: 170 } },
                field: { clearable: true },
              }}
              format="dd MMM yyyy"
            />
          </Box>

          {/* При keepPreviousData счётчик показывает прошлый total — затемняем, пока грузится свежий */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ minWidth: 'fit-content', opacity: isFetching ? 0.4 : 1, transition: 'opacity 0.2s' }}
          >
            {totalCount} {totalCount === 1 ? 'record' : 'records'}
          </Typography>
        </Box>
      </Paper>
    );
  },
);

DeletedUsersFilters.displayName = 'DeletedUsersFilters';
